import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import { serializeEditorState } from './editorSerializer';
import { serializeQuestionSetPayload } from './questionSerializerEditor';
import type {
  AddOrUpdateQuestionSetSavePayload,
  EditorState,
  RawAddOrUpdateQuestionSetPayload,
  RawComponent,
  RawQuestion,
  RawUpdateCoursePayload,
  UpdateCourseSavePayload,
} from './editorTypes';

export const contentEditorQueryKey = (courseId: string, contentId: string) =>
  ['content-editor', courseId, contentId] as const;

interface SaveComponentInput {
  componentId: string;
  courseId: string;
  editorState: EditorState;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function unwrapSavedComponent(
  response:
    | RawUpdateCoursePayload
    | LegacyKuduResponseEnvelope<RawUpdateCoursePayload>
    | RawComponent
    | null
    | undefined,
  fallbackComponent: RawComponent,
): RawComponent {
  const useCandidateOrFallback = (
    candidate: RawComponent | null | undefined,
  ) =>
    candidate &&
    Array.isArray(candidate.sections) &&
    candidate.sections.length > 0
      ? candidate
      : fallbackComponent;

  if (response && typeof response === 'object' && 'Response' in response) {
    return unwrapSavedComponent(
      response.Response as RawUpdateCoursePayload | RawComponent,
      fallbackComponent,
    );
  }

  if (
    response &&
    typeof response === 'object' &&
    'components' in response &&
    Array.isArray(response.components)
  ) {
    const [firstComponent] = response.components;
    return useCandidateOrFallback(firstComponent as RawComponent | undefined);
  }

  if (response && typeof response === 'object' && 'component' in response) {
    return useCandidateOrFallback(
      response.component as RawComponent | null | undefined,
    );
  }

  return useCandidateOrFallback(response as RawComponent | null | undefined);
}

function unwrapQuestionSetResponse(
  response:
    | RawAddOrUpdateQuestionSetPayload
    | LegacyKuduResponseEnvelope<RawAddOrUpdateQuestionSetPayload>
    | null
    | undefined,
  fallbackPayload: AddOrUpdateQuestionSetSavePayload,
) {
  if (response && typeof response === 'object' && 'Response' in response) {
    return unwrapQuestionSetResponse(
      response.Response as RawAddOrUpdateQuestionSetPayload,
      fallbackPayload,
    );
  }

  return {
    preLectureQuestions:
      response?.pre_lecture_questions?.filter(Boolean) ?? fallbackPayload.pre_lecture_questions,
    questions: response?.question?.filter(Boolean) ?? fallbackPayload.question,
  };
}

export function useSaveComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<RawComponent, ApiError, SaveComponentInput>({
    mutationFn: async ({ componentId, courseId, editorState }) => {
      if (!user) {
        throw createDataError('You must be signed in to save content.');
      }

      const currentUserId = getKuduUserId(user) ?? user.uid;

      if (!currentUserId) {
        throw createDataError('Unable to determine the current Kudu user ID.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      let finalizedQuestions: RawQuestion[] | undefined;
      let finalizedPreLectureQuestions: RawQuestion[] | undefined;

      if (
        editorState.questionSectionOwnerSectionId ||
        editorState.preLectureQuestionSectionOwnerSectionId
      ) {
        const questionPayload = serializeQuestionSetPayload(
          editorState,
          currentUserId,
          courseId,
        );
        const questionResponse = await client.invokeKuduMethod<
          | RawAddOrUpdateQuestionSetPayload
          | LegacyKuduResponseEnvelope<RawAddOrUpdateQuestionSetPayload>,
          AddOrUpdateQuestionSetSavePayload
        >('AddOrUpdateQuestionSet', questionPayload);

        const unwrappedQuestions = unwrapQuestionSetResponse(
          questionResponse,
          questionPayload,
        );

        finalizedQuestions = unwrappedQuestions.questions;
        finalizedPreLectureQuestions = unwrappedQuestions.preLectureQuestions;
      }

      const payload: UpdateCourseSavePayload = serializeEditorState(
        editorState,
        courseId,
        {
          preLectureQuestions: finalizedPreLectureQuestions,
          questions: finalizedQuestions,
        },
      );

      if (
        !Array.isArray(payload.components) ||
        payload.components.length === 0 ||
        !Array.isArray(payload.sections) ||
        payload.sections.length === 0
      ) {
        throw createDataError(
          'Content editor saves require a full reconstructed component and top-level sections.',
        );
      }

      const [component] = payload.components;

      const response = await client.invokeKuduMethod<
        RawUpdateCoursePayload | LegacyKuduResponseEnvelope<RawUpdateCoursePayload>,
        UpdateCourseSavePayload
      >('UpdateCourse', payload);

      const savedComponent = unwrapSavedComponent(response, component);

      await queryClient.invalidateQueries({
        exact: false,
        queryKey: ['component', courseId, componentId],
      });
      await queryClient.invalidateQueries({
        queryKey: contentEditorQueryKey(courseId, componentId),
      });

      return savedComponent;
    },
  });
}
